/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { get } from 'lodash'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  DescriptionTable,
  HiddenPasswordField,
  NotFound,
  Page,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
  Notification
} from 'shared/components'
import { sendErrorReport, displayValue } from 'shared/helpers'
import { fetchLicenseUser } from 'src/license/actions'
import { deleteLicenseUser } from 'src/LicenseUsers/actions'
import OrderManagerForm from 'src/order/OrderContainer/components/OrderManagers/components/OrderManagerForm'
import OrderManagerPasswordForm from 'src/order/OrderContainer/components/OrderManagers/components/OrderManagerPasswordForm'
import LicensesTab from './LicensesTab'
import UserGroupsTab from './UserGroupsTab'
import DevicesTab from './DevicesTab'
import './styles.scss'

const LicenseUser = () => {
  const history = useHistory()
  const { userID, companyId } = useParams()

  const [isLoading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [user, setUser] = useState(null)

  const [isEditUserDisplayed, setEditUserDisplayed] = useState(false)
  const [showUserPasswordForm, setUserPasswordForm] = useState(false)

  const [showDeleteUserConfirmation, setDeleteUserConfirmationDisplay] = useState(false)
  const [deletingUser, setDeletingUser] = useState(false)

  const fetchUser = useCallback(() => {
    fetchLicenseUser(userID, companyId)
      .then(res => {
        const data = get(res, 'data')
        setUser(data)
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get user details')
        setLoading(false)
        setNotFound(true)
      })
  }, [companyId, userID])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  const handleDeleteUser = () => {
    setDeletingUser(true)

    // eslint-disable-next-line no-undef
    deleteLicenseUser(userID, companyId)
      .then(() => {
        Notification('success', __('Changes saved successfully'))
        history.push(`/${companyId}/license_users`)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot delete license user')
        setDeletingUser(false)

        // Extract the specific error message from the backend response
        const errorMessage =
          get(err, 'response.data.detail') ||
          get(err, 'response.data.message') ||
          __('There was an error while saving your changes')

        Notification('error', __('Your changes were not saved'), errorMessage)
      })
  }

  const displayInitialPassword = () => {
    const hasInitialPass = get(user, 'is_initial_password')
    const initialPass = get(user, 'initial_password')
    if (!hasInitialPass) {
      return __('N/A')
    }
    return <HiddenPasswordField value={initialPass} fallback={__('N/A')} />
  }

  if (isLoading) {
    return (
      <Page>
        <ContentLoader text={__('Getting license user details')} />
      </Page>
    )
  }
  if (notFound) {
    return (
      <Page>
        <NotFound />
      </Page>
    )
  }

  return (
    <Page title={get(user, 'true_email')}>
      <div className='list-header'>
        <div>
          <Button theme='info' onClick={() => setEditUserDisplayed(true)}>
            {__('Edit license user')}
          </Button>
        </div>
        <div>
          <Button theme='default' onClick={() => setUserPasswordForm(true)}>
            {__('Change password')}
          </Button>
          <Button theme='error' onClick={() => setDeleteUserConfirmationDisplay(true)}>
            {__('Delete')}
          </Button>
        </div>
      </div>
      <div className='LicenseUser'>
        <DescriptionTable
          details={[
            { label: __('Email'), value: displayValue(get(user, 'true_email')) },
            { label: __('First Name'), value: displayValue(get(user, 'first_name')) },
            { label: __('Last Name'), value: displayValue(get(user, 'last_name')) },
            { label: __('Phone number'), value: displayValue(get(user, 'phone_number')) },
            { label: __('Initial password'), value: displayInitialPassword() }
          ]}
        />
        <Tabs>
          <TabsHeader>
            <Tab>{__('Licenses')}</Tab>
            <Tab>{__('Devices')}</Tab>
            <Tab>{__('User groups')}</Tab>
          </TabsHeader>
          <TabContent>
            <div className='licenses-tab'>
              <LicensesTab />
            </div>
          </TabContent>
          <TabContent>
            <div className='devices-tab'>
              <DevicesTab />
            </div>
          </TabContent>
          <TabContent>
            <div className='userGroups-tab'>
              <UserGroupsTab />
            </div>
          </TabContent>
        </Tabs>
      </div>
      {isEditUserDisplayed && (
        <OrderManagerForm
          companyID={companyId}
          closeCb={() => setEditUserDisplayed(false)}
          confirmCb={() => {
            fetchUser()
            setEditUserDisplayed(false)
          }}
          manager={user}
        />
      )}
      {showUserPasswordForm && (
        <OrderManagerPasswordForm
          companyID={companyId}
          closeCb={() => {
            setUserPasswordForm(false)
          }}
          refetchManagers={fetchUser}
          manager={user}
        />
      )}
      {showDeleteUserConfirmation && (
        <ConfirmationPopup
          closeCb={() => setDeleteUserConfirmationDisplay(false)}
          confirmCb={handleDeleteUser}
          title={__('Are you sure you want to delete this license user?')}
          confirmText={__('Delete')}
          theme='error'
          disabled={deletingUser}
        >
          {__('License user will be removed from any orders he is assigned to.')}
        </ConfirmationPopup>
      )}
    </Page>
  )
}

export default LicenseUser
